import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, retry } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { HttpService } from 'src/app/services/http.service';
import { SharedService } from 'src/app/services/shared.service';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('footerEl', { static: false }) footerEl!: ElementRef;

  data: Observable<any>;
  user: Observable<any>;
  areas: any;
  today: number = Date.now();
  restaurant_address: string;
  isShowScheduleClick = false;
  footerScheduleRaw: any[] = [];
  footerSchedule: any[] = [];
  about_content: string;
  isScreenLg = false;
  services: any[] = [];

  private restaurantId: string | null = null;
  private intersectionObserver?: IntersectionObserver;
  private dataSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private footerScheduleLoaded = false;
  private footerScheduleLoading = false;
  private platformDataLoaded = false;

  constructor(
    private store: StoreService,
    private http: HttpService,
    private sharedService: SharedService,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private cd: ChangeDetectorRef,
    private router: Router
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.isScreenLg = window.innerWidth > 575;
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.onResize();
    this.data = this.store.restaurantData;
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isShowScheduleClick = false;
      });

    //  Reactively respond to restaurant ID changes
    this.dataSubscription = this.data
      .pipe(
        filter(d => d && Object.keys(d).length > 0),
        map(d => d?.restaurant_data?.restaurant_id),
        distinctUntilChanged()
      )
      .subscribe(resId => {
        if (resId) {
          const isRestaurantChanged =
            !!this.restaurantId && this.restaurantId !== resId;

          if (isRestaurantChanged) {
            this.footerScheduleLoaded = false;
            this.footerScheduleRaw = [];
            this.footerSchedule = [];
          }

          this.restaurantId = resId;

          this.prepareRestaurantAddress();

          // The restaurant ID can arrive after the footer has already intersected.
          // Load here as well so a cold visit cannot miss the schedule request.
          this.loadFooterSchedule(resId);

          // Only re-fetch other APIs if the restaurant actually changed.
          if (isRestaurantChanged) {
            this.loadOtherPlatformData(this.restaurantId);
          }
        }
      });

    //  Load About content (optional)
    if (sessionStorage.getItem('restaurant_id')) {
      this.http.getPageDataById_V2(2).subscribe((res: any) => {
        this.about_content = res?.sub_headline;
      });
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    //  Wait until footer actually appears in DOM
    const waitForFooter = () => {
      if (this.footerEl?.nativeElement) {
        this.setupObserver();
      } else {
        setTimeout(waitForFooter, 300);
      }
    };
    waitForFooter();
  }

  setupObserver(): void {
    if (!this.footerEl) return;

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && this.restaurantId) {
          if (!this.footerScheduleLoaded) {
            this.loadFooterSchedule(this.restaurantId);
          }
          if (!this.platformDataLoaded) {
            this.loadOtherPlatformData(this.restaurantId);
            this.platformDataLoaded = true;
          }
          this.intersectionObserver?.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px 200px 0px'
      }
    );

    this.intersectionObserver.observe(this.footerEl.nativeElement);
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.dataSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  prepareRestaurantAddress(): void {
    this.data.subscribe(d => {
      let res_postcode = '';
      if (d?.restaurant_data?.street_address)
        res_postcode += ', ' + d.restaurant_data.street_address.trim();
      if (d?.restaurant_data?.local_area)
        res_postcode += ', ' + d.restaurant_data.local_area.trim();
      if (d?.restaurant_data?.city_or_town)
        res_postcode += ', ' + d.restaurant_data.city_or_town.trim();
      if (d?.restaurant_data?.postcode)
        res_postcode += ' ' + d.restaurant_data.postcode.trim();
      this.restaurant_address = res_postcode.replace(/^,/, '').trim();
      this.areas = d?.restaurant_delivery_areas;
      this.cd.detectChanges();
    });
  }

  //  Load restaurant opening schedule
  loadFooterSchedule(resId: any): void {
    if (!resId || this.footerScheduleLoaded || this.footerScheduleLoading) return;

    this.footerScheduleLoading = true;
    this.http.fetchRestaurantSchedule(resId)
      .pipe(
        retry(2),
        finalize(() => {
          this.footerScheduleLoading = false;
        })
      )
      .subscribe(
        (res: any) => {
          if (res && res.status) {
            const rawSchedule = res?.data || [];
            this.footerScheduleRaw = rawSchedule;
            const formattedData =
              this.sharedService.formattedFullSchedule(rawSchedule) || [];
            this.footerSchedule = formattedData.sort((a: any, b: any) => {
              const orderA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
              const orderB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
              return orderA - orderB;
            });
            this.footerScheduleLoaded = true;
            this.cd.detectChanges();
          }
        },
        () => {
          // Keep footerScheduleLoaded false so a later visibility event can retry.
        }
      );
  }

  //  Toggle schedule visibility
  showScheduleClick() {
    this.isShowScheduleClick = !this.isShowScheduleClick;
  }

  //  Load delivery platform services
  loadOtherPlatformData(resId: any): void {
    this.http.getAllPlatformServices(resId).subscribe((res: any) => {
      if (res && res.status) {
        this.services = res?.services;
      }
    });
  }

  getFooterTodaySchedule(): string {
    if (!this.footerScheduleRaw || this.footerScheduleRaw.length === 0) {
      return '';
    }
    return this.sharedService.getScheduleDisplayLabel(this.footerScheduleRaw);
  }
}
